import { MenuItemProps } from '@televet/kibble-ui';
import { Box, Flex, useDisclosure } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import cuid from 'cuid';
import { BaseEmoji } from 'emoji-mart';
import 'emoji-mart/css/emoji-mart.css';
import AttachDocumentationModal from 'pages/Conversations/components/ChannelView/MessageComposer/Attachments/Documentation/AttachDocumentationModal';
import DispatchAutomationButton from 'pages/Conversations/components/ChannelView/MessageComposer/Toolbar/DispatchAutomationButton';
import { IAttachmentOption } from 'pages/Conversations/interfaces/IAttachmentOption';
import { IKeyboardShortcutElementRefs } from 'pages/Conversations/interfaces/IKeyboardShortcutElementRefs';
import { CountryCode } from 'pages/Settings/pages/GeneralInfoSettings';
import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import checkBrowserSupport, {
  getTooltipMessage,
} from 'shared/components/UnsupportedBrowserPopover/checkBrowserSupport';
import { PersistedStateKeys } from 'shared/enums';
import { CallTypes } from 'shared/enums/CallTypes';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import useClinicUser from 'shared/hooks/useClinicUser';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import usePersistedState from 'shared/hooks/usePersistedState';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { usePhoneCallContext } from 'shared/providers/PhoneCallProvider';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import {
  ChannelMessageAttachmentEntityType,
  ChannelMessageAttachmentType,
  ChannelViewChannelFragment,
  ChannelViewClinicPetParentFragment,
  ClinicPimsIntegrationType,
  ClinicUserClinicFragment,
  FormTemplateType,
  MessageType,
  PaymentRelationship,
} from 'shared/types/graphql';
import { getClinicPetParentDisplayName, selectPrimaryPhoneNumberBestGuess } from 'shared/utils';
import { convertFileToAttachment, createUniqueId } from '../../../../utils';
import InvoiceAttachmentPopover from '../Attachments/Invoice/InvoiceAttachmentPopover';
import AttachmentsMenu from './AttachmentsMenu';
import AudioCallMenu from './AudioCallMenu';
import EmojiPickerModal from './EmojiPickerModal';
import GiphyButton from './GiphyButton';
import MessageComposerDivider from './MessageComposerDivider';
import MessageTemplateMenu from './MessageTemplateMenu';
import { getPetParentsForContact } from './utils';

interface IProps {
  channel?: ChannelViewChannelFragment | null;
  clinicPetParents: ChannelViewClinicPetParentFragment[];
  clinic: ClinicUserClinicFragment | null;
  messageType: MessageType;
  attachments: IAttachmentOption[];
  stagedFiles: File[];
  inputTextareaRef: React.MutableRefObject<HTMLTextAreaElement | null>;
  isChannelLoading: boolean;
  isSendingMessage: boolean;
  isTeamChannel: boolean;
  isInvoicingDrawerOpen: boolean;
  onInvoicingDrawerClose: () => void;
  onInvoicingDrawerToggle: () => void;
  setAttachments: (value: React.SetStateAction<IAttachmentOption[]>) => void;
  setStagedFiles: (value: React.SetStateAction<File[]>) => void;
  setDraftMessageText: (value: React.SetStateAction<string>) => void;
  onEmojiSelect: (emoji: BaseEmoji) => void;
  onSendMessage: () => void;
  isEmojiPickerOpen: boolean;
  setIsEmojiPickerOpen: (value: boolean) => void;
  setHasPotentialBenefits: React.Dispatch<React.SetStateAction<boolean>>;
  hasPotentialBenefits: boolean;
  setIsLinkPetToFormModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setInvoiceForSidePanelId: React.Dispatch<React.SetStateAction<string>>;
  setAttachInvoiceClicked?: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedFormAttachment: React.Dispatch<React.SetStateAction<IAttachmentOption | undefined>>;
  setShouldOpenOpenFormModal: React.Dispatch<React.SetStateAction<boolean>>;
  isOverlay?: boolean;
  shortcuts: IKeyboardShortcutElementRefs;
}

const maxImageCount = 6;
const maxCombinedVideoSize = 100000000; // 100 MB

const MessageComposerToolbar = ({
  channel,
  clinicPetParents,
  clinic,
  messageType,
  attachments,
  stagedFiles,
  inputTextareaRef,
  isChannelLoading,
  isSendingMessage,
  isTeamChannel,
  isInvoicingDrawerOpen,
  onInvoicingDrawerClose,
  onInvoicingDrawerToggle,
  setAttachments,
  setStagedFiles,
  onEmojiSelect,
  setDraftMessageText,
  onSendMessage,
  isEmojiPickerOpen,
  setIsEmojiPickerOpen,
  setHasPotentialBenefits,
  hasPotentialBenefits,
  setInvoiceForSidePanelId,
  setIsLinkPetToFormModalOpen,
  setAttachInvoiceClicked,
  setSelectedFormAttachment,
  setShouldOpenOpenFormModal,
  shortcuts,
  isOverlay,
}: IProps): JSX.Element => {
  const { clinicUser, currentClinic } = useClinicUser();
  const { allActiveIntegrations, isInvoiceDiscoverySupported } = useIntegrationsProvider();
  const { toastMessagePop } = useCustomToast();
  const { trackEvent } = useTracking();

  const { isFeatureEnabled, isFeatureFlagsLoading } = useFeatureFlag();

  const acceptedFileTypes = `image/*, video/*, *.mp4, video/mp4, video/x-m4v, .pdf, .doc, .docx, .gif, .txt, audio/*, .zip, .7z, */dicom, .dcm, */dcm, .dicom`;

  // TODO: Move to integrations provider as 'hasActiveBitwerx' integration
  const isBitwerxIntegrated = useMemo(() => {
    if (allActiveIntegrations) {
      for (const integration of allActiveIntegrations) {
        if (integration.type === ClinicPimsIntegrationType.Bitwerx) {
          return true;
        }
      }
    }
    return false;
  }, [allActiveIntegrations]);

  const hasPimsPetParent = useMemo(() => {
    return clinicPetParents.some((parent) => parent.pimsId !== null);
  }, [clinicPetParents]);

  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const toast = useToast();
  const { search, pathname } = useLocation();
  const history = useHistory();
  const { stageOutgoingPhoneCall } = usePhoneCallContext();
  const { isMobile } = useResolutionProvider();

  const {
    isOpen: isDocumentationModalOpen,
    onOpen: onDocumentationModalOpen,
    onClose: onDocumentationModalClose,
  } = useDisclosure();

  const { isBrowserSupported, OS } = useMemo(() => {
    return checkBrowserSupport();
  }, []);

  const submerchantStripeId = clinic?.submerchantStripeId || '';
  const submerchantStripeChargesEnabled = clinic?.submerchantStripeChargesEnabled || false;

  const hasCustomizableFees = useMemo(
    () => !!currentClinic?.clinicSetting?.hasCustomizableFees,
    [currentClinic?.clinicSetting?.hasCustomizableFees],
  );

  const handleFileUploadClick = useCallback(() => {
    if (inputFileRef?.current) {
      inputFileRef.current.click();
    }
  }, [inputFileRef]);

  const onGiphyAttachmentSelect = useCallback(
    (attachment: IAttachmentOption): void => {
      setAttachments((prevAttachments) => {
        return prevAttachments.concat(attachment);
      });
    },
    [setAttachments],
  );

  const onRemoveAllInvoices = useCallback(() => {
    setAttachments((prevAttachments) => {
      return prevAttachments.filter((a) => a.value.attachmentType !== ChannelMessageAttachmentType.Invoice);
    });
  }, [setAttachments]);

  const onAttachInvoiceWithAmount = useCallback(
    (
      amount: string,
      invoiceIds: string[] = [] as string[],
      isBalanceSelected: boolean,
      discounts?: string,
      paymentRelationship?: PaymentRelationship,
    ) => {
      setAttachments((prevAttachments) => {
        const newPrevAttachments = prevAttachments.filter((a) => {
          return a.value.attachmentType !== ChannelMessageAttachmentType.Invoice;
        });

        const args: IAttachmentOption = {
          id: createUniqueId(),
          text: 'Invoice',
          invoiceIds,
          clientServiceFeePercent:
            (hasCustomizableFees && clinic?.clinicSetting?.paymentFeeConfig?.onlineClientServiceFeePercent) || 0,
          invoiceAmount: amount === '0.00' ? '' : amount.replace(',', ''),
          isBalanceSelected,
          invoiceTotalDiscounts: discounts ? discounts.replace(',', '') : '',
          value: {
            id: cuid(),
            attachmentType: ChannelMessageAttachmentType.Invoice,
          },
          validationError: parseFloat(amount) < 0.5 ? 'Payment amount must be greater or equal to $0.50' : undefined,
        };

        if (!!paymentRelationship) {
          args.paymentRelationship = paymentRelationship;
        }

        return newPrevAttachments.concat(args);
      });
    },
    [setAttachments, clinic, hasCustomizableFees],
  );

  const validateTotalVideoSize = (videoSize: number): boolean => {
    return videoSize <= maxCombinedVideoSize;
  };

  const validateTotalImageCount = (imageList: File[]): boolean => {
    return imageList.length <= maxImageCount;
  };

  const onCheckFileDuplication = useCallback(
    (newFiles: File[]) => {
      const stagedFileNames = stagedFiles.map((file) => file.name);

      const checkNewFiles = newFiles.filter((file) => !stagedFileNames.includes(file.name));
      return checkNewFiles;
    },
    [stagedFiles],
  );

  const updateSelectedFiles = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = e.target.files;
    if (!files) return;

    const newFiles: File[] = Object.entries(files).map(([, file]) => file);
    const checkedNewFile = onCheckFileDuplication(newFiles);
    if (checkedNewFile.length === 0) {
      toastMessagePop(
        `${
          newFiles.length === 1 ? 'This file has' : 'These files have'
        } already been attached. Please try another one.`,
        STATUSES.info,
        2000,
      );
      e.target.value = '';
      return;
    } else if (checkedNewFile.length < newFiles.length && newFiles.length > 1) {
      toastMessagePop('Some of the files have already been attached.', STATUSES.info, 2000);
    }

    const allFiles = stagedFiles.concat(checkedNewFile);

    const imageList = allFiles?.filter((file: File) => file?.type?.includes('image'));
    const videoList = allFiles?.filter((file: File) => file?.type?.includes('video'));

    if (videoList.length > 0) {
      const totalVideoSize = videoList
        .map((video: File) => {
          return video.size;
        })
        .reduce((prev, curr) => {
          return prev + curr;
        });

      if (!validateTotalVideoSize(totalVideoSize)) {
        toast({
          status: 'error',
          title: 'Videos must not exceed 100 MB combined',
        });
        return;
      }
    }

    if (!validateTotalImageCount(imageList)) {
      toast({
        status: 'error',
        title: `Maximum ${maxImageCount} images allowed per message`,
      });
      return;
    }

    const newAttachments = attachments.concat(checkedNewFile.map((file: File) => convertFileToAttachment(file)));
    setAttachments(newAttachments);
    setStagedFiles(allFiles);
    e.target.value = '';
  };

  const { petParentOptions, petParentOptionsMap } = useMemo(() => {
    return getPetParentsForContact(clinicPetParents);
  }, [clinicPetParents]);

  const isPhoneNumberValid = (parent: ChannelViewClinicPetParentFragment): boolean => {
    return !!selectPrimaryPhoneNumberBestGuess(parent.phoneNumbers || []);
  };

  const onStartPhoneCall = useCallback(
    (parent: ChannelViewClinicPetParentFragment): void => {
      if (!(parent?.phoneNumbers?.length > 0)) {
        return;
      }

      stageOutgoingPhoneCall({ clinicPetParentId: parent.id });
    },
    [stageOutgoingPhoneCall],
  );

  const onRequestVideoCallSelect = useCallback(() => {
    setAttachments((prevAttachments) => {
      return prevAttachments.concat({
        id: createUniqueId(),
        text: 'Video call request',
        value: {
          attachmentType: ChannelMessageAttachmentType.CallRequest,
          entityType: ChannelMessageAttachmentEntityType.Appointment,
        },
      } as IAttachmentOption);
    });
  }, [setAttachments]);

  useEffect(() => {
    if (
      search.includes('sendVideo') &&
      !attachments.find((attachment) => attachment?.value?.attachmentType === ChannelMessageAttachmentType.CallRequest)
    ) {
      if (search.includes('sendVideo') && !!channel) history.push(pathname.replace('?sendVideo=true', ''));
      // There is a race condition between loading the message and
      // adding the video request when coming from the right panel,
      // I added a timeOut with zero delay to move these two
      // processes lower in the stack to ensure its execution
      setTimeout(() => {
        onRequestVideoCallSelect();
      }, 0);
    }
  }, [search, channel, onRequestVideoCallSelect, attachments, history, pathname]);

  const phoneTooltipMessage = useMemo(() => {
    if (!isBrowserSupported) {
      return getTooltipMessage(CallTypes.Phone, OS);
    }

    const phoneNumber = selectPrimaryPhoneNumberBestGuess(clinicPetParents[0]?.phoneNumbers || []);
    const clientName = getClinicPetParentDisplayName(clinicPetParents[0] || {});
    return phoneNumber ? `Call ${clientName}` : `${clientName} does not have a saved phone number.`;
  }, [isBrowserSupported, clinicPetParents, OS]);

  const petIds = useMemo(() => {
    return channel?.pets?.map((p) => p.id);
  }, [channel]);

  const [isDeveloperModeEnabled] = usePersistedState(PersistedStateKeys.IsDeveloperModeEnabled, false);
  const [isAutomationsSenderEnabled] = usePersistedState(PersistedStateKeys.IsAutomationsSenderEnabled, false);
  const [isDiscoveredInvoiceDemoModeEnabled] = usePersistedState(
    PersistedStateKeys.IsDiscoveredInvoiceDemoModeEnabled,
    false,
  );

  const attachmentsHasCallRequest = useMemo(
    () =>
      attachments.some((attachment) => attachment.value.attachmentType === ChannelMessageAttachmentType.CallRequest),
    [attachments],
  );

  const isUSClinic = useMemo(() => {
    if (!clinicUser?.vetInfo?.currentClinic?.country) return true;
    return clinicUser?.vetInfo?.currentClinic?.country === CountryCode.US;
  }, [clinicUser?.vetInfo?.currentClinic?.country]);

  const canSendInvoices =
    !isTeamChannel &&
    messageType !== MessageType.Note &&
    isUSClinic &&
    submerchantStripeId &&
    submerchantStripeChargesEnabled;

  const displayInvoiceAsPopover = useMemo(
    () =>
      (isBitwerxIntegrated || isDiscoveredInvoiceDemoModeEnabled) &&
      isInvoiceDiscoverySupported &&
      !isFeatureFlagsLoading &&
      isFeatureEnabled(FeatureFlagName.InvoiceDiscovery) &&
      hasPimsPetParent,
    [
      isBitwerxIntegrated,
      isDiscoveredInvoiceDemoModeEnabled,
      isInvoiceDiscoverySupported,
      isFeatureFlagsLoading,
      isFeatureEnabled,
      hasPimsPetParent,
    ],
  );

  const onTemplateSelection = useCallback(
    (name: string, body: string, attachments: IAttachmentOption[]) => {
      if (attachments.length > 0) {
        setAttachments((prevAttachments) => [...prevAttachments, ...attachments]);
      }
      setDraftMessageText((value) => (value ? `${value}\n${body}` : body));
      inputTextareaRef.current?.focus();
      trackEvent(ConversationEvents.MessageTemplateSelected, { templateName: name });
    },
    [inputTextareaRef, setAttachments, setDraftMessageText, trackEvent],
  );

  const onFormSelection = useCallback(
    (attachment: IAttachmentOption, entityId: string, isSelected: boolean, formType?: FormTemplateType) => {
      setAttachments((prevAttachments) => {
        if (!isSelected) {
          return prevAttachments.concat(attachment);
        }
        return prevAttachments.filter((a) => a.value.entityId !== entityId);
      });

      setIsLinkPetToFormModalOpen(true);
      if (formType === FormTemplateType.FillInTheBlank) {
        setSelectedFormAttachment(attachment);
        setShouldOpenOpenFormModal(true);
      }
      inputTextareaRef?.current?.focus();
    },
    [
      setAttachments,
      setIsLinkPetToFormModalOpen,
      setSelectedFormAttachment,
      setShouldOpenOpenFormModal,
      inputTextareaRef,
    ],
  );

  const onInvoiceAttachmentSelection = useCallback(
    (attachment: IAttachmentOption) => {
      setAttachments((prevAttachments) => {
        return prevAttachments.concat(attachment);
      });
    },
    [setAttachments],
  );

  return (
    <Flex alignItems="center" justifyContent="space-between" paddingX="6px" className="MessageComposerToolBar">
      <Flex alignItems="center">
        {!isTeamChannel && (isAutomationsSenderEnabled || isDeveloperModeEnabled) && (
          <>
            <DispatchAutomationButton clinicPetIds={petIds} smallButton={isOverlay} />
            <MessageComposerDivider />
          </>
        )}
        <MessageTemplateMenu
          smallButton={isOverlay}
          onSelection={onTemplateSelection}
          channel={channel}
          isTeamChannel={isTeamChannel}
          shortcuts={shortcuts}
        />
        <MessageComposerDivider />
        <AttachmentsMenu
          smallButton={isOverlay}
          isTeamChannel={isTeamChannel}
          canSendInvoices={canSendInvoices}
          displayInvoiceAsPopover={displayInvoiceAsPopover}
          onInvoicingDrawerToggle={onInvoicingDrawerToggle}
          handleFileUploadClick={handleFileUploadClick}
          isMobile={isMobile}
          onDocumentationModalOpen={onDocumentationModalOpen}
          attachments={attachments}
          setAttachInvoiceClicked={setAttachInvoiceClicked}
          onFormSelection={onFormSelection}
          onInvoiceAttachmentSelection={onInvoiceAttachmentSelection}
          shortcuts={shortcuts}
        />
        {canSendInvoices && displayInvoiceAsPopover && (
          <InvoiceAttachmentPopover
            isDrawerOpen={isInvoicingDrawerOpen}
            onDrawerClose={onInvoicingDrawerClose}
            onDrawerToggle={onInvoicingDrawerToggle}
            clinicPetParents={clinicPetParents}
            onInvoiceAttach={onAttachInvoiceWithAmount}
            onRemoveAllInvoices={onRemoveAllInvoices}
            selectedInvoiceIds={selectedInvoiceIds}
            setSelectedInvoiceIds={setSelectedInvoiceIds}
            setHasPotentialBenefits={setHasPotentialBenefits}
            hasPotentialBenefits={hasPotentialBenefits}
            setInvoiceForSidePanelId={setInvoiceForSidePanelId}
          />
        )}
        {!isMobile && (
          <AttachDocumentationModal
            setDraftMessageText={setDraftMessageText}
            isOpen={isDocumentationModalOpen}
            onClose={onDocumentationModalClose}
            onOpen={onDocumentationModalOpen}
          />
        )}
        {(!isTeamChannel || !isMobile) && <MessageComposerDivider />}
        {isTeamChannel ? null : petParentOptions?.length > 1 ? (
          <AudioCallMenu
            isOverlay={isOverlay}
            petParentOptions={petParentOptions}
            petParentOptionsMap={petParentOptionsMap}
            onStartPhoneCall={onStartPhoneCall}
          />
        ) : (
          <Box>
            <Tooltip label={phoneTooltipMessage}>
              <Button
                variant="ghostNeutral"
                isDisabled={!isPhoneNumberValid(clinicPetParents[0] || {}) || !isBrowserSupported}
                data-testid="start-phone-call-button"
                size={isOverlay ? 'sm' : 'md'}
                iconName="phone"
                onClick={async (e: MouseEvent): Promise<void> => {
                  const parent = petParentOptionsMap.get(petParentOptions[0].value || '');
                  if (!e || !parent) {
                    return;
                  }
                  onStartPhoneCall(parent);
                }}
              />
            </Tooltip>
          </Box>
        )}
        <Tooltip label="Video Call">
          <Button
            {...(!attachmentsHasCallRequest && { onClick: onRequestVideoCallSelect })}
            data-testid="request-video-call-button"
            isDisabled={attachmentsHasCallRequest}
            size={isOverlay ? 'sm' : 'md'}
            variant="ghostNeutral"
            iconName="videoCamera"
          />
        </Tooltip>

        <input
          style={{ display: 'none' }}
          multiple
          type="file"
          name="file"
          id="file-input"
          accept={acceptedFileTypes}
          ref={inputFileRef}
          onChange={(e): Promise<void> => updateSelectedFiles(e)}
        />

        {!isMobile && (
          <>
            <MessageComposerDivider />
            <GiphyButton shortcuts={shortcuts} onGifSelection={onGiphyAttachmentSelect} />
            <EmojiPickerModal
              isEmojiPickerOpen={isEmojiPickerOpen}
              onEmojiSelect={onEmojiSelect}
              setIsEmojiPickerOpen={setIsEmojiPickerOpen}
              shortcuts={shortcuts}
            />
          </>
        )}
      </Flex>
      {isMobile && <Button onClick={onSendMessage} iconName="send" variant="ghost" isLoading={isSendingMessage} />}

      {!isMobile && (
        <Button
          data-testid="message-composer-send-button"
          disabled={isChannelLoading}
          isLoading={isSendingMessage}
          onClick={onSendMessage}
          size="sm"
          variant={messageType === MessageType.Note ? 'secondary' : 'primary'}
        >
          {messageType === MessageType.Note ? 'Make Note' : 'Send Message'}
        </Button>
      )}
    </Flex>
  );
};

export default React.memo(MessageComposerToolbar);

export type AudioCallMenuProps = {
  isOverlay?: boolean;
  petParentOptions: MenuItemProps[];
  petParentOptionsMap: Map<string, ChannelViewClinicPetParentFragment>;
  onStartPhoneCall: (parent: ChannelViewClinicPetParentFragment) => void;
};
